import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/redux/store";

import { fetchCreateCard } from "@/redux/profile/cards/asyncActions";
import { addUserCardRedux } from "@/redux/profile/cards/slice";
import { fetchGetCards } from "@/redux/profile/cards/asyncActions";
import { axiosInstance } from "@/resources/requestInfo";

import styles from "./AddCardPayment.module.scss";
import { ProcessingSkeleton } from "./ProcessingSkeleton";
import { fetchProfileInfo } from "@/redux/profile/settings/getProfileInfo/asyncActions"
import { isTinkoffProvider } from "@/utils/payment/paymentProvider";
import { cardsRoutes } from "@/redux/profile/cards/routes";

// Глобальный тип для виджета YooKassa
declare global {
  interface Window {
    YooMoneyCheckoutWidget?: any;
  }
}

function loadYooKassaWidget(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.YooMoneyCheckoutWidget) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://yookassa.ru/checkout-widget/v1/checkout-widget.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load YooKassa widget script'));
    document.head.appendChild(script);
  });
}

export const AddCardPayment: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { profileInfo } = useAppSelector(state => state.getProfile);

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [widgetRendered, setWidgetRendered] = useState(false);
  const [confirmationToken, setConfirmationToken] = useState<string | null>(null);
  // Т-Касса: привязка идёт формой банка (AddCard), результат — по RequestKey.
  const [tinkoffFormUrl, setTinkoffFormUrl] = useState<string | null>(null);
  const isTinkoff = isTinkoffProvider();

  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const widgetInstanceRef = useRef<any>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const paymentIdRef = useRef<string | null>(null);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const destroyWidget = () => {
    if (widgetInstanceRef.current) {
      try {
        widgetInstanceRef.current.destroy();
      } catch (e) {
        // ignore
      }
      widgetInstanceRef.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      destroyWidget();
    };
  }, []);

  const processSuccessfulPayment = async (paymentId: string) => {
    try {
      setIsProcessingPayment(true);

      // Get payment info to extract card data
      const statusRes = await axiosInstance.get(`/payments/${paymentId}/status`);
      const paymentData = statusRes.data;

      const token = paymentData.payment_method?.id;
      const last4 = paymentData.payment_method?.card?.last4;

      if (!token || !last4) {
        throw new Error("Не удалось извлечь данные карты из платежа");
      }

      let currentProfileInfo = profileInfo;
      if (!currentProfileInfo?.id) {
        await dispatch(fetchProfileInfo());
      }

      const userId = currentProfileInfo?.id;
      if (!userId) {
        throw new Error("Не удалось определить ID пользователя");
      }

      const cardTitle = `**** **** **** ${last4}`;
      const newCard = await dispatch(
        fetchCreateCard({
          user_id: userId,
          token: token,
          title: cardTitle,
        })
      ).unwrap();

      dispatch(addUserCardRedux(newCard));

      // Refund the 11 RUB test payment
      try {
        await axiosInstance.post(`/payments/${paymentId}/refund`);
      } catch (refundError) {
        console.warn("Не удалось вернуть тестовый платеж:", refundError);
      }

      await dispatch(fetchGetCards());

      // Close tab if opened from CardList, otherwise navigate to cards
      if (window.opener) {
        try {
          window.opener.postMessage('card-binding-success', window.location.origin);
        } catch (e) {
          console.warn('Не удалось отправить сообщение родительскому окну:', e);
        }
        window.close();
      } else {
        navigate("/profile/cards");
      }
    } catch (err: any) {
      console.error("Ошибка при обработке платежа:", err);
      setError(err.message || "Ошибка при обработке платежа");
      setIsProcessingPayment(false);
    }
  };

  const finishBinding = async () => {
    await dispatch(fetchGetCards());

    // Close tab if opened from CardList, otherwise navigate to cards
    if (window.opener) {
      try {
        window.opener.postMessage('card-binding-success', window.location.origin);
      } catch (e) {
        console.warn('Не удалось отправить сообщение родительскому окну:', e);
      }
      window.close();
    } else {
      navigate("/profile/cards");
    }
  };

  /** Т-Касса: опрос состояния заявки на привязку (GetAddCardState). */
  const startTinkoffPolling = (requestKey: string) => {
    stopPolling();

    let attempts = 0;
    const maxAttempts = 200;

    pollingRef.current = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        stopPolling();
        setError("Превышено время ожидания привязки карты");
        return;
      }

      try {
        const res = await axiosInstance.get(cardsRoutes.TINKOFF_CARD_STATE(requestKey));
        const status = res.data?.status;

        if (status === 'succeeded') {
          stopPolling();
          setIsProcessingPayment(true);
          await finishBinding();
        } else if (status === 'canceled') {
          stopPolling();
          setError("Карту привязать не удалось");
        }
      } catch (err) {
        // Ignore polling errors
      }
    }, 3000);
  };

  const startPolling = (paymentId: string) => {
    stopPolling();

    let attempts = 0;
    const maxAttempts = 200;

    pollingRef.current = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        stopPolling();
        setError("Превышено время ожидания привязки карты");
        return;
      }

      try {
        const res = await axiosInstance.get(`/payments/${paymentId}/status`);
        const status = res.data?.status;

        if (status === 'succeeded' && !isProcessingPayment) {
          stopPolling();
          await processSuccessfulPayment(paymentId);
        } else if (status === 'canceled') {
          stopPolling();
          setError("Платеж был отменен");
        }
      } catch (err) {
        // Ignore polling errors
      }
    }, 3000);
  };

  // Phase 1: Create payment, get confirmation token
  useEffect(() => {
    const initializePayment = async () => {
      try {
        setIsLoading(true);

        await dispatch(fetchProfileInfo());

        if (isTinkoff) {
          const tinkoffRes = await axiosInstance.post(cardsRoutes.TINKOFF_CARDS);
          const { request_key, payment_url } = tinkoffRes.data || {};

          if (!request_key || !payment_url) {
            setError("Не удалось получить форму привязки карты");
            return;
          }

          setTinkoffFormUrl(payment_url);
          startTinkoffPolling(request_key);
          return;
        }

        const res = await axiosInstance.post('/payments/bind-card', {
          phone: profileInfo?.phone || '',
        });

        const { id, confirmation_token } = res.data;

        if (!confirmation_token) {
          setError("Не удалось получить токен для привязки карты");
          return;
        }

        paymentIdRef.current = id;
        startPolling(id);
        setConfirmationToken(confirmation_token);
      } catch (err: any) {
        console.error("Ошибка при создании платежа:", err);
        setError(err.response?.data?.message || err.message || "Не удалось создать платеж для привязки карты");
      } finally {
        setIsLoading(false);
      }
    };

    initializePayment();
  }, []);

  // Phase 2: Render widget after container is in DOM (isLoading=false)
  useEffect(() => {
    if (!confirmationToken || isTinkoff) return;

    const renderWidget = async () => {
      try {
        await loadYooKassaWidget();

        const checkout = new window.YooMoneyCheckoutWidget({
          confirmation_token: confirmationToken,
          error_callback: (widgetError: any) => {
            console.error('YooKassa widget error:', widgetError);
            stopPolling();
            setError("Ошибка виджета оплаты");
          },
        });

        widgetInstanceRef.current = checkout;

        await checkout.render('add-card-widget-container');
        setWidgetRendered(true);
      } catch (err: any) {
        console.error("Ошибка при рендере виджета:", err);
        setError(err.message || "Не удалось загрузить форму оплаты");
      }
    };

    renderWidget();
  }, [confirmationToken]);

  if (error) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.error}>
          <p className={styles.errorText}>{error}</p>
          <button
            className={styles.backButton}
            onClick={() => navigate("/profile/cards")}
          >
            Вернуться к картам
          </button>
        </div>
      </div>
    );
  }

  if (isProcessingPayment) {
    return (
      <div className={styles.wrapper}>
        <ProcessingSkeleton text="Сохранение карты..." />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.wrapper}>
        <ProcessingSkeleton text="Создание платежа..." />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <p className={styles.title}>Добавление карты</p>
        <p className={styles.description}>
          {isTinkoff
            ? "Карта привязывается в защищённой форме банка. Для проверки банк временно заблокирует небольшую сумму и сразу вернёт её."
            : "Для привязки карты необходимо произвести тестовый платеж в 11 рублей. Средства будут возвращены автоматически."}
        </p>
        {isTinkoff ? (
          tinkoffFormUrl ? (
            <>
              <iframe
                title="Привязка карты Т-Касса"
                src={tinkoffFormUrl}
                className={styles.bindingFrame}
                allow="payment *"
              />
              <p className={styles.bindingFallback}>
                Форма не открылась?{' '}
                <a href={tinkoffFormUrl} target="_blank" rel="noopener noreferrer">
                  Открыть в новом окне
                </a>
              </p>
            </>
          ) : (
            <ProcessingSkeleton text="Загрузка формы привязки..." />
          )
        ) : (
          <>
            <div
              id="add-card-widget-container"
              ref={widgetContainerRef}
              style={{ display: widgetRendered ? 'block' : 'none' }}
            />
            {!widgetRendered && (
              <ProcessingSkeleton text="Загрузка формы оплаты..." />
            )}
          </>
        )}
      </div>
    </div>
  );
};
